<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { formatDate24h } from '@/utils/dateFormatter'
import { useToast } from '@/composables/useToast'

// import type { CallList } from '@/types/calllist'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'CallList',
  },
})

const { t } = useI18n()
const router = useRouter()
const { showToast } = useToast()

// 👉 Data
const isDialogVisible = ref(false)
const isEditDialogVisible = ref(false)
const currentCallList = ref<any>(null)
const saving = ref(false)

// 👉 Table headers
const headers = computed(() => [
  {
    title: t('calllist.importKey'),
    key: 'importKey',
    sortable: true,
  },
  {
    title: t('calllist.listName'),
    key: 'listName',
    sortable: true,
  },
  {
    title: t('calllist.totalRecords'),
    key: 'totalRecords',
    sortable: true,
  },
  {
    title: t('calllist.importedLeadsCount'),
    key: 'importedLeadsCount',
    sortable: true,
  },
  {
    title: t('calllist.status'),
    key: 'status',
    sortable: true,
  },
  {
    title: t('common.isActive'),
    key: 'isActive',
    sortable: true,
  },
  {
    title: t('calllist.startDate'),
    key: 'startDate',
    sortable: true,
  },
  {
    title: t('calllist.endDate'),
    key: 'endDate',
    sortable: true,
  },
  {
    title: t('common.createdAt'),
    key: 'createdAt',
    sortable: true,
  },
  {
    title: t('common.actions'),
    key: 'actions',
    sortable: false,
  },
])

// 👉 Form data
const callListForm = ref({
  importKey: '',
  listName: '',
  listDetail: '',
  listComment: '',
  fileName: '',
  totalRecords: 0,
  status: 'uploaded',
  isActive: true,
  startDate: null,
  endDate: null,
})

// 👉 Search and filter options
const searchQuery = ref('')
const selectedStatus = ref()
const selectedIsActive = ref()

// Status options
const statusOptions = computed(() => [
  { title: t('calllist.statusPending'), value: 'uploaded' },
  { title: t('calllist.statusProcessing'), value: 'processing' },
  { title: t('calllist.statusCompleted'), value: 'completed' },
  { title: t('calllist.statusFailed'), value: 'failed' },
  { title: t('calllist.statusPaused'), value: 'paused' },
])

// IsActive options
const isActiveOptions = computed(() => [
  { title: t('common.active'), value: true },
  { title: t('common.inactive'), value: false },
])

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Update data table options
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// 👉 Fetching call lists
const { data: callListsData, execute: loadCallLists, isFetching: loading } = useApi<any>(createUrl('/call-lists', {
  query: {
    q: searchQuery,
    status: selectedStatus,
    isActive: selectedIsActive,
    itemsPerPage,
    page,
    sortBy,
    orderBy,
  },
}))

const callLists = computed(() => callListsData.value?.data || [])
const totalItems = computed(() => callListsData.value?.total || 0)

// 👉 Open add dialog - redirect to create page
const openAddDialog = () => {
  router.push('/calllist/create')
}

// 👉 Open edit dialog
const openEditDialog = (callList: any) => {
  currentCallList.value = callList

  callListForm.value = {
    importKey: '',
    listName: callList.listName || '',
    listDetail: callList.listDetail || '',
    listComment: callList.listComment || '',
    fileName: '',
    totalRecords: 0,
    status: callList.status || 'uploaded',
    isActive: callList.isActive !== undefined ? callList.isActive : true,
    startDate: callList.startDate ? formatDate24h(callList.startDate, 'YYYY-MM-DD') : null,
    endDate: callList.endDate ? formatDate24h(callList.endDate, 'YYYY-MM-DD') : null,
  }
  isEditDialogVisible.value = true
}

// 👉 Close dialogs
const closeAddDialog = () => {
  isDialogVisible.value = false
  callListForm.value = {
    importKey: '',
    listName: '',
    listDetail: '',
    listComment: '',
    fileName: '',
    totalRecords: 0,
    status: 'uploaded',
    isActive: true,
    startDate: null,
    endDate: null,
  }
}

const closeEditDialog = () => {
  isEditDialogVisible.value = false
  currentCallList.value = null
  callListForm.value = {
    importKey: '',
    listName: '',
    listDetail: '',
    listComment: '',
    fileName: '',
    totalRecords: 0,
    status: 'uploaded',
    isActive: true,
    startDate: null,
    endDate: null,
  }
}

// 👉 Save call list
const saveCallList = async () => {
  try {
    saving.value = true

    // Prepare data for API
    const apiData = {
      ...callListForm.value,

      // Keep status as string (no conversion needed)
      status: callListForm.value.status,

      // Convert dates to ISO format if they exist
      startDate: callListForm.value.startDate ? new Date(callListForm.value.startDate).toISOString() : null,
      endDate: callListForm.value.endDate ? new Date(callListForm.value.endDate).toISOString() : null,
    }

    if (currentCallList.value) {
      // Update existing call list
      await $api(`/call-lists/${currentCallList.value.id}`, {
        method: 'PATCH',
        body: apiData,
      })
      showToast({
        type: 'success',
        message: t('calllist.callListUpdatedSuccessfully'),
      })
    }
    else {
      // Create new call list
      await $api('/call-lists', {
        method: 'POST',
        body: apiData,
      })
      showToast({
        type: 'success',
        message: t('calllist.callListCreatedSuccessfully'),
      })
    }

    await loadCallLists()
    closeAddDialog()
    closeEditDialog()
  }
  catch (error) {
    console.error('Failed to save call list:', error)
    showToast({
      type: 'error',
      message: t('calllist.error'),
    })
  }
  finally {
    saving.value = false
  }
}

// 👉 Delete call list
const deleteCallList = async (callList: any) => {
  try {
    await $api(`/call-lists/${callList.id}`, {
      method: 'DELETE',
    })
    showToast({
      type: 'success',
      message: t('calllist.callListDeletedSuccessfully'),
    })
    await loadCallLists()
  }
  catch (error) {
    console.error('Failed to delete call list:', error)
    showToast({
      type: 'error',
      message: t('calllist.error'),
    })
  }
}

// 👉 Get status text
const getStatusText = (status: string) => {
  const option = statusOptions.value.find(opt => opt.value === status)

  return option?.title || 'Unknown'
}

// 👉 Get status color
const getStatusColor = (status: string) => {
  switch (status) {
  case 'uploaded':
    return 'warning'
  case 'processing':
    return 'info'
  case 'completed':
    return 'success'
  case 'failed':
    return 'error'
  case 'paused':
    return 'secondary'
  default:
    return 'grey'
  }
}

// 👉 Load data on mount
onMounted(() => {
  loadCallLists()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('navigation.calllist') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('calllist.manageCallLists') }}
      </p>
    </div>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('common.filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select Status -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedStatus"
              :placeholder="t('calllist.selectStatus')"
              :items="statusOptions"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
          <!-- 👉 Select IsActive -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedIsActive"
              :placeholder="t('common.selectStatus')"
              :items="isActiveOptions"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: t('common.all') },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 15.625rem;">
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('calllist.searchCallLists')"
            />
          </div>

          <!-- 👉 Add call list button -->
          <VBtn
            prepend-icon="tabler-plus"
            @click="openAddDialog"
          >
            {{ t('common.add') }} {{ t('calllist.callList') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="callLists"
        :items-length="totalItems"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- 👉 Import Key -->
        <template #item.importKey="{ item }">
          <div class="d-flex align-center gap-x-4">
            <VAvatar
              color="primary"
              variant="tonal"
              size="34"
            >
              <VIcon icon="tabler-key" />
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ (item as any).importKey || '' }}
              </h6>
            </div>
          </div>
        </template>

        <!-- 👉 List Name -->
        <template #item.listName="{ item }">
          <div class="d-flex flex-column">
            <h6 class="text-base">
              {{ (item as any).listName || '' }}
            </h6>
            <div class="text-sm text-medium-emphasis">
              {{ (item as any).listDetail || t('common.noDescription') }}
            </div>
          </div>
        </template>

        <!-- 👉 Total Records -->
        <template #item.totalRecords="{ item }">
          <span class="text-sm">
            {{ (item as any).totalRecords || 0 }}
          </span>
        </template>

        <!-- 👉 Imported Leads Count -->
        <template #item.importedLeadsCount="{ item }">
          <VChip
            :color="(item as any).importedLeadsCount > 0 ? 'success' : 'default'"
            size="small"
            variant="tonal"
          >
            <VIcon icon="tabler-database-import" size="16" class="me-1" />
            {{ (item as any).importedLeadsCount || 0 }}
          </VChip>
        </template>

        <!-- 👉 Status -->
        <template #item.status="{ item }">
          <VChip
            :color="getStatusColor((item as any).status)"
            size="small"
            label
            class="text-capitalize"
          >
            {{ getStatusText((item as any).status) }}
          </VChip>
        </template>

        <!-- 👉 Is Active -->
        <template #item.isActive="{ item }">
          <VChip
            :color="(item as any).isActive ? 'success' : 'error'"
            size="small"
            variant="tonal"
          >
            {{ (item as any).isActive ? t('common.active') : t('common.inactive') }}
          </VChip>
        </template>

        <!-- 👉 Start Date -->
        <template #item.startDate="{ item }">
          <span class="text-sm">
            {{ (item as any).startDate ? formatDate24h((item as any).startDate) : t('common.notSet') }}
          </span>
        </template>

        <!-- 👉 End Date -->
        <template #item.endDate="{ item }">
          <span class="text-sm">
            {{ (item as any).endDate ? formatDate24h((item as any).endDate) : t('common.notSet') }}
          </span>
        </template>

        <!-- 👉 Created At -->
        <template #item.createdAt="{ item }">
          <span class="text-sm">
            {{ formatDate24h((item as any).createdAt) }}
          </span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn @click="openEditDialog(item)">
            <VIcon icon="tabler-pencil" />
          </IconBtn>

          <IconBtn @click="deleteCallList(item)">
            <VIcon icon="tabler-trash" />
          </IconBtn>
        </template>

        <!-- pagination -->
        <template #bottom>
          <TablePagination
            v-model:page="page"
            :items-per-page="itemsPerPage"
            :total-items="totalItems"
          />
        </template>
      </VDataTableServer>
    <!-- SECTION -->
    </VCard>

    <!-- 👉 Add Dialog -->
    <VDialog
      v-model="isDialogVisible"
      max-width="600"
    >
      <VCard>
        <VCardTitle>
          {{ t('common.add') }} {{ t('calllist.callList') }}
        </VCardTitle>

        <VCardText>
          <VForm @submit.prevent="saveCallList">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="callListForm.importKey"
                  :label="t('calllist.importKey')"
                  :placeholder="t('calllist.enterImportKey')"
                  required
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="callListForm.listName"
                  :label="t('calllist.listName')"
                  :placeholder="t('calllist.enterListName')"
                  required
                />
              </VCol>

              <VCol cols="12">
                <AppTextarea
                  v-model="callListForm.listDetail"
                  :label="t('calllist.listDetail')"
                  :placeholder="t('calllist.enterListDetail')"
                />
              </VCol>

              <VCol cols="12">
                <AppTextarea
                  v-model="callListForm.listComment"
                  :label="t('calllist.listComment')"
                  :placeholder="t('calllist.enterListComment')"
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="callListForm.fileName"
                  :label="t('calllist.fileName')"
                  :placeholder="t('calllist.enterFileName')"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model.number="callListForm.totalRecords"
                  :label="t('calllist.totalRecords')"
                  type="number"
                  min="0"
                />
              </VCol>

              <VCol cols="6">
                <AppSelect
                  v-model="callListForm.status"
                  :label="t('calllist.status')"
                  :items="statusOptions"
                />
              </VCol>

              <VCol cols="6">
                <VSwitch
                  v-model="callListForm.isActive"
                  :label="t('common.isActive')"
                  color="success"
                  inset
                />
              </VCol>

              <VCol cols="6">
                <AppDateTimePicker
                  v-model="callListForm.startDate"
                  :label="t('calllist.startDate')"
                  :placeholder="t('calllist.selectStartDate')"
                  type="date"
                />
              </VCol>

              <VCol cols="6">
                <AppDateTimePicker
                  v-model="callListForm.endDate"
                  :label="t('calllist.endDate')"
                  :placeholder="t('calllist.selectEndDate')"
                  type="date"
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="closeAddDialog"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveCallList"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Edit Dialog -->
    <VDialog
      v-model="isEditDialogVisible"
      max-width="800"
    >
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon
            icon="tabler-edit"
            class="me-2"
          />
          {{ t('common.edit') }} {{ t('calllist.callList') }}
        </VCardTitle>

        <VCardText>
          <VForm @submit.prevent="saveCallList">
            <VRow>
              <!-- Left Column: Editable Form -->
              <VCol
                cols="12"
                md="6"
              >
                <VCard
                  variant="outlined"
                  class="h-100"
                >
                  <VCardTitle class="text-h6">
                    <VIcon
                      icon="tabler-edit"
                      class="me-2"
                    />
                    {{ t('calllist.editableFields') }}
                  </VCardTitle>
                  <VCardText>
                    <VRow>
                      <VCol cols="12">
                        <AppTextField
                          v-model="callListForm.listName"
                          :label="t('calllist.listName')"
                          :placeholder="t('calllist.enterListName')"
                          required
                        />
                      </VCol>

                      <VCol cols="12">
                        <AppTextarea
                          v-model="callListForm.listDetail"
                          :label="t('calllist.listDetail')"
                          :placeholder="t('calllist.enterListDetail')"
                        />
                      </VCol>

                      <VCol cols="12">
                        <AppTextarea
                          v-model="callListForm.listComment"
                          :label="t('calllist.listComment')"
                          :placeholder="t('calllist.enterListComment')"
                        />
                      </VCol>

                      <VCol cols="12">
                        <AppDateTimePicker
                          v-model="callListForm.startDate"
                          :label="t('calllist.startDate')"
                          :placeholder="t('calllist.selectStartDate')"
                          type="date"
                        />
                      </VCol>

                      <VCol cols="12">
                        <AppDateTimePicker
                          v-model="callListForm.endDate"
                          :label="t('calllist.endDate')"
                          :placeholder="t('calllist.selectEndDate')"
                          type="date"
                        />
                      </VCol>

                      <VCol cols="12">
                        <AppSelect
                          v-model="callListForm.status"
                          :label="t('calllist.status')"
                          :items="statusOptions"
                        />
                      </VCol>

                      <VCol cols="12">
                        <VSwitch
                          v-model="callListForm.isActive"
                          :label="t('common.isActive')"
                          color="success"
                          inset
                        />
                      </VCol>
                    </VRow>
                  </VCardText>
                </VCard>
              </VCol>

              <!-- Right Column: Read-only Information -->
              <VCol
                cols="12"
                md="6"
              >
                <VCard
                  variant="outlined"
                  class="h-100"
                >
                  <VCardTitle class="text-h6">
                    <VIcon
                      icon="tabler-info-circle"
                      class="me-2"
                    />
                    {{ t('calllist.callListInformation') }}
                  </VCardTitle>
                  <VCardText>
                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.importKey') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.importKey || t('common.notSet') }}
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.fileName') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.fileName || t('common.notSet') }}
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.fileType') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.fileType || t('common.notSet') }}
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.fileSize') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.fileSize ? `${(currentCallList.fileSize / 1024).toFixed(2)} KB` : t('common.notSet') }}
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.totalRecords') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.totalRecords || 0 }}
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.importedLeadsCount') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        <VChip
                          :color="(currentCallList?.importedLeadsCount || 0) > 0 ? 'success' : 'default'"
                          size="small"
                          variant="tonal"
                        >
                          <VIcon icon="tabler-database-import" size="16" class="me-1" />
                          {{ currentCallList?.importedLeadsCount || 0 }}
                        </VChip>
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.badList') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.badList || 0 }}
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.inlistDup') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.inlistDup || 0 }}
                      </div>
                    </div>

                    <!-- DNC Groups -->
                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.dncGroups') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        <template v-if="currentCallList?.callListDncGroups && currentCallList.callListDncGroups.length > 0">
                          <div class="d-flex flex-wrap gap-2 mt-2">
                            <VChip
                              v-for="dncGroup in currentCallList.callListDncGroups"
                              :key="dncGroup.id"
                              color="error"
                              size="small"
                              variant="tonal"
                            >
                              <VIcon icon="tabler-ban" size="16" class="me-1" />
                              {{ dncGroup.dncGroup?.name || 'Unknown' }}
                            </VChip>
                          </div>
                        </template>
                        <template v-else>
                          <span class="text-medium-emphasis">{{ t('common.notSet') }}</span>
                        </template>
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.indbDup') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.indbDup || 0 }}
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('calllist.dncCount') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        <VChip
                          :color="(currentCallList?.dncCount || 0) > 0 ? 'error' : 'default'"
                          size="small"
                          variant="tonal"
                        >
                          <VIcon icon="tabler-ban" size="16" class="me-1" />
                          {{ currentCallList?.dncCount || 0 }}
                        </VChip>
                      </div>
                    </div>

                    <div class="mb-4">
                      <label class="text-caption text-medium-emphasis">
                        {{ t('common.createdAt') }}
                      </label>
                      <div class="text-body-1 font-weight-medium">
                        {{ currentCallList?.createdAt ? formatDate24h(currentCallList.createdAt) : t('common.notSet') }}
                      </div>
                    </div>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="closeEditDialog"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveCallList"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
